import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "@/api/endpoints";
import { useAuthStore } from "@/store/authStore";
import { useToast } from "@/components/shared/Toast";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);
  const { setAuth } = useAuthStore();
  const showToast = useToast((s) => s.show);
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    auth
      .setupRequired()
      .then((r) => setNeedsSetup(r.setup_required))
      .catch(() => setNeedsSetup(false));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    try {
      if (needsSetup) {
        await auth.setup({ username, password });
      }
      const { access_token } = await auth.login(username, password);
      const me = await (async () => {
        useAuthStore.getState().setToken(access_token);
        return auth.me();
      })();
      setAuth(me, access_token);
      const dest = (location.state as { from?: string } | null)?.from || "/inventory";
      nav(dest, { replace: true });
    } catch (err) {
      showToast(err instanceof Error ? err.message : "login failed", "error");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:bg-slate-900 dark:border-slate-800"
      >
        <div>
          <h1 className="text-lg font-semibold">fleet-tracker</h1>
          <p className="text-sm text-slate-500">
            {needsSetup ? "Create the first admin account" : "Sign in"}
          </p>
        </div>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Username</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:bg-slate-800 dark:border-slate-700"
            required
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:bg-slate-800 dark:border-slate-700"
            required
            minLength={needsSetup ? 12 : 1}
          />
          {needsSetup && (
            <span className="text-xs text-slate-500">Min 12 characters.</span>
          )}
        </label>
        <button
          type="submit"
          disabled={pending}
          className="w-full rounded-md bg-brand-500 px-3 py-2 text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {pending ? "…" : needsSetup ? "Create admin" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
